import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Label } from '@nooz-optics/nooz-components-lib';
import { IInputForm } from '../../../../utils/types/contentful';
import { Body, Button } from '../../Elements';
import { Link } from '../../Elements/Link';
import * as Inputs from '../Inputs';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { alertState } from 'utils/recoil/snackbar';

export interface IFormProps extends IInputForm {
  defaultValues: any;
  onSubmit: Function;
  resolver?: any;
  errorLabels?: Record<string, any>[];
}

export const Form = ({
  body,
  defaultValues,
  inputs,
  onChange,
  onSubmit,
  onSubmitSecondary,
  secondaryButton,
  secondaryLink,
  submitButton,
  resolver,
  errorLabels,
}: IFormProps) => {
  const setAlert = useSetRecoilState(alertState);
  // Init form state
  const {
    handleSubmit,
    register,
    getValues,
    reset,
    watch,
    formState: { errors },
    clearErrors,
  } = useForm<FormData>({
    resolver,
    defaultValues,
  });

  // Reset form on defaultValues change
  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues]);

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      let error: any = Object.values(errors);
      error = error[0];
      error = errorLabels.find((e) => e.key === error?.type);

      setAlert((prev) => ({
        ...prev,
        show: true,
        type: 'error',
        title: (error || errorLabels[0])?.text,
      }));
      clearErrors();
    }
  }, [errors]);

  const handleFormSubmit = handleSubmit((data) => {
    onSubmit(data);
  });

  const handleFormChange = () => onChange && onChange(getValues());

  const returnFormInputs = () =>
    inputs?.map((input) => {
      const Input = Inputs[input.reference];
      // Insert Label in input list
      if (input.text) {
        return <Label text={input.text} />;
      }

      if (Input) {
        return (
          <div className='sm:col-span-6'>
            <Input
              {...input}
              label={{
                ...input.label,
                text: input?.label
                  ? `${input?.label?.text}${input.required ? ' *' : ''}`
                  : undefined,
              }}
              register={register}
              watch={watch}
            />
          </div>
        );
      }
    });

  return (
    <form
      onChange={handleFormChange}
      onSubmit={handleFormSubmit}
      className='w-full space-y-8 text-left'>
      {/* Form Body */}
      {body && (
        <div className='mb-6'>
          <Body {...body} />
        </div>
      )}
      {/* Form inputs  */}
      <div className='grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6'>
        {returnFormInputs()}
      </div>
      {/* Submit Button */}
      {submitButton && (
        <div className='pt-5'>
          <div className='sm:flex sm:justify-between'>
            <Button
              {...submitButton}
              size='base'
              type='contained'
              color={{ classname: submitButton.color?.classname || 'primary' }}
              htmlType='submit'
            />
            {secondaryButton && (
              <div className='pt-2 sm:pt-0 sm:pl-4'>
                <Button
                  onClick={onSubmitSecondary}
                  {...secondaryButton}
                  size='base'
                  type='outlined'
                  color={{ classname: 'primary' }}
                />
              </div>
            )}
          </div>
          {secondaryLink && (
            <div className='mt-2'>
              <Link
                {...secondaryLink}
                label={{
                  ...secondaryLink.label,
                  color: { classname: 'gray-600' },
                  size: 'sm',
                  decoration: 'underline',
                }}
              />
            </div>
          )}
        </div>
      )}
    </form>
  );
};

export const TextInputForm = ({
  defaultValues,
  inputs,
  onChange,
  onSubmit,
  submitButton,
}: IFormProps) => {
  const { handleSubmit, register, getValues, reset } = useForm<FormData>({
    defaultValues,
  });

  // Reset form on defaultValues change
  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues]);

  const handleFormSubmit = handleSubmit((data) => {
    onSubmit(data);
  });

  const handleFormChange = () => onChange && onChange(getValues());

  return (
    <form
      onChange={handleFormChange}
      onSubmit={handleFormSubmit}
      className='w-full space-y-8 divide-y text-left'>
      <div className='flex w-full items-center'>
        <Inputs.TextInput
          {...inputs[0]}
          register={register}
          button={{
            ...submitButton,
            size: 'xs',
            type: 'contained',
            htmlType: 'submit',
          }}
        />
      </div>
    </form>
  );
};
